import React, { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import styles from '../styles/PublicLegalPage.module.css';
import PublicLegalFooter from '../components/PublicLegalFooter';

const SMS_CONSENT_LABEL = (
  <>
    I <strong>optionally</strong> agree to receive SMS messages from LOTW League at the mobile
    number provided above, including verification codes, password reset codes, and optional pick
    reminders. I understand message frequency varies (max 4 msgs/month) and that message and data
    rates may apply. See our{' '}
    <Link to="/privacy" className={styles.link}>Privacy Policy</Link>
    {' '}for how we handle phone numbers.
  </>
);

const SmsOptInPage = () => {
  const navigate = useNavigate();
  const { login, error: authError } = useContext(AuthContext);

  const [step, setStep] = useState('opt-in');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);
  const [formError, setFormError] = useState('');

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isSigningIn, setIsSigningIn] = useState(false);

  const handleOptInSubmit = (event) => {
    event.preventDefault();
    setFormError('');

    if (!phoneNumber.trim()) {
      setFormError('Please enter your mobile phone number.');
      return;
    }

    if (!consentChecked) {
      setFormError('You must check the consent box to opt in to SMS messages.');
      return;
    }

    setStep('sign-in');
  };

  const handleSignInSubmit = async (event) => {
    event.preventDefault();
    setFormError('');
    setIsSigningIn(true);

    try {
      const result = await login({ username, password });
      if (result.passwordResetRequired) {
        navigate('/reset-password');
      } else {
        navigate('/user-settings');
      }
    } catch {
      // Auth error is surfaced via AuthContext
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleBackToOptIn = () => {
    setFormError('');
    setUsername('');
    setPassword('');
    setStep('opt-in');
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h1 className={styles.title}>SMS Opt-In (Optional)</h1>
        <p className={styles.subtitle}>
          LOTW League — optional transactional text messages
        </p>

        <p className={styles.complianceBanner}>
          <strong>SMS is completely optional.</strong> You may use all LOTW League features — picks,
          standings, league management — without providing a mobile phone number or opting in to SMS.
        </p>

        <p className={styles.skipProminent}>
          <Link to="/login" className={styles.skipLink}>Skip SMS — Sign in without text messages</Link>
        </p>

        <section className={styles.section}>
          {step === 'opt-in' ? (
            <>
              <h2>Optional SMS Enrollment</h2>
              <p>
                Only complete the form below if you <strong>want</strong> SMS verification codes,
                password reset texts, or optional pick reminders. If you do not want text messages,
                use the skip link above to sign in with no phone number required.
              </p>

              <form className={styles.optInForm} onSubmit={handleOptInSubmit} noValidate>
                <div className={styles.formField}>
                  <label htmlFor="sms-phone-number">
                    Mobile Phone Number <span className={styles.optionalTag}>(optional)</span>
                  </label>
                  <input
                    id="sms-phone-number"
                    name="phoneNumber"
                    type="tel"
                    autoComplete="tel"
                    placeholder="(555) 123-4567"
                    value={phoneNumber}
                    onChange={(event) => setPhoneNumber(event.target.value)}
                    className={styles.formInput}
                  />
                  <p className={styles.formHint}>
                    Max 4 messages per month. Message and data rates may apply.
                  </p>
                </div>

                <div className={styles.consentField}>
                  <label className={styles.consentLabel}>
                    <input
                      type="checkbox"
                      name="smsConsent"
                      checked={consentChecked}
                      onChange={(event) => setConsentChecked(event.target.checked)}
                      className={styles.consentCheckbox}
                    />
                    <span className={styles.consentText}>{SMS_CONSENT_LABEL}</span>
                  </label>
                </div>

                {formError && (
                  <p className={styles.formError} role="alert">
                    {formError}
                  </p>
                )}

                <button type="submit" className={styles.formButton}>
                  Opt In to SMS &amp; Continue to Sign In
                </button>

                <p className={styles.skipSecondary}>
                  <Link to="/login" className={styles.skipLink}>No thanks — Continue without SMS</Link>
                </p>

                <p className={styles.formNote}>
                  Checking the consent box and providing a phone number is{' '}
                  <strong>not required</strong> to create an account, sign in, or use LOTW League.
                  SMS is used only for account security and optional pick reminders — no marketing
                  messages. General site terms are in our{' '}
                  <Link to="/terms" className={styles.link}>Terms of Service</Link>
                  {' '}and are separate from SMS opt-in.
                </p>
              </form>
            </>
          ) : (
            <>
              <h2>Sign In to Complete Opt-In</h2>
              <p>
                You chose to opt in to SMS. Sign in below to finish verification in User Settings,
                or{' '}
                <Link to="/login" className={styles.link}>sign in without SMS</Link>.
              </p>

              <div className={styles.capturedSummary}>
                <p className={styles.capturedSummaryTitle}>Your opt-in details</p>
                <dl className={styles.capturedSummaryList}>
                  <div>
                    <dt>Mobile phone number</dt>
                    <dd>{phoneNumber}</dd>
                  </div>
                  <div>
                    <dt>SMS consent</dt>
                    <dd>Agreed</dd>
                  </div>
                </dl>
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={handleBackToOptIn}
                >
                  Edit opt-in details
                </button>
              </div>

              <form className={styles.optInForm} onSubmit={handleSignInSubmit} noValidate>
                <div className={styles.formField}>
                  <label htmlFor="sms-opt-in-username">Username</label>
                  <input
                    id="sms-opt-in-username"
                    name="username"
                    type="text"
                    autoComplete="username"
                    value={username}
                    onChange={(event) => setUsername(event.target.value)}
                    className={styles.formInput}
                    disabled={isSigningIn}
                    required
                  />
                </div>

                <div className={styles.formField}>
                  <label htmlFor="sms-opt-in-password">Password</label>
                  <input
                    id="sms-opt-in-password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    className={styles.formInput}
                    disabled={isSigningIn}
                    required
                  />
                </div>

                {(formError || authError) && (
                  <p className={styles.formError} role="alert">
                    {formError || authError}
                  </p>
                )}

                <button
                  type="submit"
                  className={styles.formButton}
                  disabled={isSigningIn}
                >
                  {isSigningIn ? 'Signing in...' : 'Sign In & Complete Opt-In'}
                </button>

                <p className={styles.formNote}>
                  Need an account? Ask a commissioner for an invite.
                  {' · '}
                  <Link to="/forgot-password" className={styles.link}>Forgot password?</Link>
                </p>
              </form>
            </>
          )}
        </section>

        <section className={styles.section}>
          <h2>Complete Opt-In in User Settings (If You Opted In)</h2>
          <p>If you chose SMS above and signed in, finish enrollment with these steps:</p>
          <ol>
            <li>Go to <strong>User Settings</strong>.</li>
            <li>Confirm your mobile phone number.</li>
            <li>Check the SMS consent box (unchecked by default).</li>
            <li>Tap <strong>Start Verification</strong> to receive a 6-digit code via SMS.</li>
            <li>Enter the code to verify your phone number.</li>
            <li>
              Optionally enable <strong>SMS Pick Reminders</strong> for lock reminders by text.
            </li>
          </ol>
          <p>
            You can skip SMS entirely and still use the app. Verification and reminders are separate
            optional steps.
          </p>
        </section>

        <section className={styles.section}>
          <h2>About SMS Messages</h2>
          <p>
            LOTW League sends <strong>transactional SMS only</strong> — no marketing or promotional
            messages:
          </p>
          <ul>
            <li>Phone verification codes (only if you opt in and enable SMS features)</li>
            <li>Password reset codes (only when you request a reset via SMS)</li>
            <li>Optional pick-reminder notifications (only if you enable SMS reminders)</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>How to Opt Out</h2>
          <ul>
            <li>
              <strong>In the app:</strong> Turn off SMS Pick Reminders in User Settings, or remove
              your phone number from your profile.
            </li>
            <li>
              <strong>By text:</strong> Reply <strong>STOP</strong> to any LOTW League message to
              unsubscribe from SMS. Reply <strong>HELP</strong> for assistance.
            </li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>Contact</h2>
          <p>
            For SMS-related questions, contact your league administrator or reply HELP to any
            message from LOTW League.
          </p>
        </section>

        <PublicLegalFooter />
      </div>
    </div>
  );
};

export default SmsOptInPage;
